import { Button } from "@mui/material";
import { Colors } from "../../../theme/v2/variants";
import { useState } from "react";

interface ButtonComponentProps {
  onClick?: (text: String, event?: any) => void;
  type?: ButtonType;
  size?: ButtonSize;
  state?: ButtonState;
  color?: string;
  text: string;
  isPressed?: boolean;
  children?: any;
  startIcon?: any;
  endIcon?: any;
  padding?: { left: number; right: number; top: number; bottom: number };
  fullWidth?: boolean;
}

export enum ButtonType {
  PRIMARY,
  SECONDARY,
  TERTIARY,
  DANGER,
  TRANSPARENT,
  FULL_TRANSPARENT,
  TRANSPARENT_DARK,
  OUTLINE,
  OUTLINE_SELECTED,
  DARK,
  OUTLINELINK,
  LINK,
  APPROVED,
  REPROVED,
  OUTLINE_GREEN,
}

export enum ButtonSize {
  SMALL,
  MEDIUM,
  LARGE,
}

export enum ButtonState {
  DEFAULT_ENABLED,
  PRESSED,
  DISABLED,
  LOADING,
}

export default function ButtonComponent(props: ButtonComponentProps) {
  const [hover, setHover] = useState(false);

  const buttonType = props.type ? props.type : ButtonType.PRIMARY;
  const buttonSize = props.size ? props.size : ButtonSize.MEDIUM;
  const buttonState = props.state ? props.state : ButtonState.DEFAULT_ENABLED;
  const isDisabled = props.state == ButtonState.DISABLED ? true : false;

  const buttonStyle: React.CSSProperties = {
    color: props.color ? props.color : Colors.primaryColor,
    textAlign: "center",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: 0.1,
    textTransform: "none",
    fontFamily: "Noto Sans",
    height: "40px",
    width: props.fullWidth ? "100%" : "auto",
    paddingLeft: props.padding?.left ? props?.padding?.left || "0px" : "16px",
    paddingRight: props.padding?.right
      ? props?.padding?.right || "0px"
      : "16px",
    paddingTop: props.padding?.top ? props?.padding?.top || "0px" : "10px",
    paddingBottom: props.padding?.bottom
      ? props?.padding?.bottom || "0px"
      : "10px",
    whiteSpace: "nowrap",
  };

  const onClick = () => {
    if (isDisabled) {
      return;
    }
    props.onClick?.(props.text);
  };

  function handleStyleByProps(): React.CSSProperties {
    if (buttonType == ButtonType.OUTLINE_GREEN) {
      return outlineGreenTypeStyle();
    }

    if (buttonType == ButtonType.TRANSPARENT) {
      return transparentTypeStyle();
    }

    if (buttonType == ButtonType.FULL_TRANSPARENT) {
      return fullTransparentTypeStyle();
    }

    if (buttonType == ButtonType.TRANSPARENT_DARK) {
      return transparentDarkTypeStyle();
    }

    if (buttonState == ButtonState.PRESSED) {
      return pressedStyle();
    }

    if (buttonState == ButtonState.DISABLED) {
      return disabledStyle();
    }

    if (buttonType == ButtonType.OUTLINE) {
      return outlineTypeStyle();
    }

    if (buttonType == ButtonType.OUTLINE_SELECTED) {
      return outlineSelectedTypeStyle();
    }

    if (buttonType == ButtonType.OUTLINELINK) {
      return outlineLinkTypeStyle();
    }

    if (buttonType == ButtonType.LINK) {
      return LinkTypeStyle();
    }

    if (buttonType == ButtonType.APPROVED) {
      return approvedStyle();
    }

    if (buttonType == ButtonType.REPROVED) {
      return reprovedStyle();
    }

    if (buttonState == ButtonState.DEFAULT_ENABLED) {
      return defaultEnabledStyle();
    }

    return buttonStyle;
  }

  function approvedStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.backgroundColor = props?.color || Colors.greenLight;
    baseStyle.borderRadius = "48px";
    baseStyle.color = Colors.neutralColorWhite;

    return baseStyle;
  }

  function reprovedStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.backgroundColor = props?.color || Colors.alertRed;
    baseStyle.borderRadius = "48px";
    baseStyle.color = Colors.neutralColorWhite;

    return baseStyle;
  }

  function pressedStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.backgroundColor = props?.color || Colors.primaryColor;
    baseStyle.borderRadius = "48px";
    baseStyle.color = Colors.neutralColorWhite;

    return baseStyle;
  }

  function LinkTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.backgroundColor = props?.color || Colors.LinkColor;
    baseStyle.borderRadius = "48px";
    baseStyle.color = Colors.neutralColorWhite;

    return baseStyle;
  }

  function defaultEnabledStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.backgroundColor =
      props?.color || Colors.primaryColor || Colors.LinkColor;
    baseStyle.borderRadius = "48px";
    baseStyle.color = Colors.neutralColorWhite;

    return baseStyle;
  }

  function transparentTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = Colors.greenLight;
    baseStyle.borderRadius = "48px";
    baseStyle.backgroundColor = "#00000000";
    if (hover) {
      baseStyle.backgroundColor = "#e6e4e8";
    }
    return baseStyle;
  }

  function transparentDarkTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = Colors.neutralGrayDark;
    baseStyle.borderRadius = "48px";
    baseStyle.backgroundColor = "#00000000";
    if (hover) {
      baseStyle.backgroundColor = "#e6e4e8";
    }
    return baseStyle;
  }

  function fullTransparentTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = props?.color || Colors.neutralGrayDark;
    baseStyle.borderRadius = "48px";
    baseStyle.backgroundColor = "none";

    return baseStyle;
  }

  function outlineTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = Colors.primaryColor;
    baseStyle.borderRadius = "70px";
    baseStyle.backgroundColor = "#00000000";
    baseStyle.border = "1px solid " + Colors.neutralGrayLight;
    baseStyle.fontWeight = 600;
    baseStyle.fontSize = "12px";
    // baseStyle.maxHeight = "35px";
    if (hover) {
      baseStyle.backgroundColor = "#e6e4e8";
    }
    delete baseStyle.height;

    return baseStyle;
  }

  function outlineSelectedTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = Colors.indigo;
    baseStyle.borderRadius = "70px";
    baseStyle.backgroundColor = Colors.periwinkle;
    baseStyle.border = "1px solid " + Colors.perano;
    baseStyle.fontWeight = 600;
    baseStyle.fontSize = "12px";
    // baseStyle.maxHeight = "35px";
    if (hover) {
      baseStyle.backgroundColor = "#e6e4e8";
    }
    delete baseStyle.height;

    return baseStyle;
  }

  function outlineGreenTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = Colors.greenLight;
    baseStyle.borderRadius = "48px";
    baseStyle.backgroundColor = "transparent";
    baseStyle.border = "1px solid " + Colors.greenLight;

    if (hover) {
      baseStyle.backgroundColor = "#e6e4e8";
    }

    return baseStyle;
  }

  function outlineLinkTypeStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.color = Colors.neutralGrayDark;
    baseStyle.borderRadius = "48px";
    baseStyle.backgroundColor = "#00000000";
    baseStyle.border = "1px solid " + Colors.neutralGrayLight;
    if (hover) {
      baseStyle.backgroundColor = "#e6e4e8";
    }

    return baseStyle;
  }

  function disabledStyle(): React.CSSProperties {
    const baseStyle = buttonStyle;
    baseStyle.backgroundColor = props?.color || Colors.neutralColor;
    baseStyle.borderRadius = "48px";
    baseStyle.color = Colors.neutralColorWhite;

    return baseStyle;
  }

  return (
    <Button
      onClick={() => onClick()}
      style={handleStyleByProps()}
      startIcon={props.startIcon}
      endIcon={props.endIcon}
      disabled={buttonState === ButtonState.DISABLED ? true : false}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {props.text}
      {props.children ? props.children : null}
    </Button>
  );
}
